import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../../services/api";
import { C, LEAD_STAGE_DEPT, LEAD_STAGE_TASKS, LEAD_STATUSES } from "../../../../constants";
import { mapLead } from "../../../../mappers";
import { getInit } from "../../../../utils";
import RichCommentInput from "../../../../shared/comments/RichCommentInput";
import { CommentContent } from "../../../../shared/comments/commentContent";
import {
  Button,
  Card,
  Chip,
  FieldGroup,
  FieldLabel,
  Input,
  KpiCard,
  ProgressBar,
  Select,
  Timeline,
  DialogPrimitives,
  SheetPrimitives,
} from "../../../../shared/ui";
import { cn } from "../../../../lib/cn";

// Phase 3a — LeadsPage migrated to Tailwind primitives + shadcn Sheet/Dialog.
// Classes still in use from legacy CSS (migrated in later phases):
//   kb-task-*   — kanban task card family (Phase 4)
//   aud-comment-* — comment list styling (Phase 3c)
// Everything else has been inlined as utilities or swapped for a primitive.

const emptyTaskDraft = { title: "", assigneeId: "", dueDate: "" };

const STATUS_HEX = {
  NEW: "#60a5fa",
  INITIAL_CONTACT: "#f59e0b",
  FIRST_MEETING_BOOKED: "#10c981",
  IN_HUBSPOT: "#a855f7",
  LOST: "#ef4444",
};

function statusColor(status) {
  return STATUS_HEX[status] || "#8fa4c8";
}

function statusTone(status) {
  return {
    NEW: "info",
    INITIAL_CONTACT: "warn",
    FIRST_MEETING_BOOKED: "success",
    IN_HUBSPOT: "plan",
    LOST: "crit",
  }[status] || "default";
}

function label(status) {
  return String(status || "").replaceAll("_", " ");
}

function makeForm() {
  return {
    name: "",
    ownerId: "",
    status: "NEW",
    referral: "",
    industry: "",
    region: "",
    estimatedValue: "",
    contactPerson: "",
  };
}

export default function LeadsPage({ leads, setLeads, setScoping, nav, showToast, currentUser, AvatarComponent }) {
  const Avt = AvatarComponent;
  const [users, setUsers] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState(() => makeForm());
  const [saving, setSaving] = useState(false);
  const [taskDraft, setTaskDraft] = useState(emptyTaskDraft);
  const [subtaskDrafts, setSubtaskDrafts] = useState({});
  const [noteDraft, setNoteDraft] = useState("");

  useEffect(() => {
    apiClient.getActiveUsers().then((r) => setUsers(Array.isArray(r?.users) ? r.users : [])).catch(() => {});
  }, []);

  useEffect(() => setNewForm(makeForm()), [currentUser]);

  const ownerMap = useMemo(() => Object.fromEntries(users.map((user) => [user.id, user])), [users]);
  const owners = useMemo(() => [...new Set(leads.map((lead) => lead.owner).filter(Boolean))], [leads]);
  const filtered = useMemo(
    () => leads.filter((lead) => (stageFilter === "" || lead.status === stageFilter) && (ownerFilter === "" || lead.owner === ownerFilter)),
    [leads, ownerFilter, stageFilter],
  );
  const activeLead = drawerLeadId ? leads.find((lead) => lead.id === drawerLeadId) : null;

  const stats = useMemo(() => ({
    total: leads.length,
    new: leads.filter((lead) => lead.status === "NEW").length,
    contact: leads.filter((lead) => lead.status === "INITIAL_CONTACT").length,
    meeting: leads.filter((lead) => lead.status === "FIRST_MEETING_BOOKED").length,
  }), [leads]);

  const totalValue = useMemo(() => leads.reduce((sum, lead) => {
    const match = String(lead.estimatedValue || "").match(/[\d,.]+/);
    return sum + (match ? parseFloat(match[0].replace(",", "")) * 1000 : 0);
  }, 0), [leads]);

  function replaceLead(updatedLead) {
    const mapped = mapLead(updatedLead);
    setLeads((state) => state.map((lead) => (lead.id === mapped.id ? mapped : lead)));
  }

  async function updateLead(leadId, patch, successTitle) {
    try {
      const updated = await apiClient.updateLead(leadId, patch);
      replaceLead(updated);
      if (successTitle) showToast(successTitle, updated.name);
      return updated;
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Lead update failed", "error");
      return null;
    }
  }

  async function createLead() {
    if (!newForm.name.trim()) return;
    setSaving(true);
    try {
      const ownerId = newForm.ownerId || currentUser?.id || "";
      const owner = ownerMap[ownerId] || currentUser;
      const payload = {
        name: newForm.name.trim(),
        ownerId,
        status: "NEW",
        referral: newForm.referral || "",
        industry: newForm.industry || "",
        region: newForm.region || "",
        estimatedValue: newForm.estimatedValue || null,
        contactPerson: newForm.contactPerson || null,
        contactEmail: null,
      };
      const tasks = (LEAD_STAGE_TASKS[newForm.status] || []).map((task) => ({
        title: task.title,
        assigneeId: owner?.id || null,
        assignee: owner?.name || "",
        dueDate: null,
        done: false,
        subtasks: [],
      }));
      const created = await apiClient.createLead({
        ...payload,
        tasks,
        stageHistory: [{ stage: "NEW", enteredAt: new Date().toISOString(), by: currentUser?.name || owner?.name || "" }],
      });
      setLeads((state) => [mapLead(created), ...state]);
      setNewForm(makeForm());
      setShowNewModal(false);
      showToast("Lead created", created.name);
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Lead creation failed", "error");
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(lead, nextStatus) {
    const stageHistory = [...(lead.stageHistory || []), { stage: nextStatus, enteredAt: new Date().toISOString(), by: currentUser?.name || lead.owner || "" }];
    await updateLead(lead.id, { status: nextStatus, stageHistory }, "Stage updated");
  }

  async function createTask() {
    if (!activeLead || !taskDraft.title.trim()) return;
    try {
      const updated = await apiClient.createLeadTask(activeLead.id, taskDraft);
      replaceLead(updated);
      setTaskDraft(emptyTaskDraft);
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Task creation failed", "error");
    }
  }

  async function updateTask(taskId, patch) {
    if (!activeLead) return;
    try {
      replaceLead(await apiClient.updateLeadTask(activeLead.id, taskId, patch));
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Task update failed", "error");
    }
  }

  async function createSubtask(taskId) {
    const draft = subtaskDrafts[taskId];
    if (!activeLead || !draft?.title?.trim()) return;
    try {
      replaceLead(await apiClient.createLeadSubtask(activeLead.id, taskId, draft));
      setSubtaskDrafts((state) => ({ ...state, [taskId]: emptyTaskDraft }));
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Subtask creation failed", "error");
    }
  }

  async function updateSubtask(taskId, subtaskId, patch) {
    if (!activeLead) return;
    try {
      replaceLead(await apiClient.updateLeadSubtask(activeLead.id, taskId, subtaskId, patch));
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Subtask update failed", "error");
    }
  }

  async function createNote({ html } = {}) {
    if (!activeLead) return;
    const text = html || noteDraft;
    if (!text?.trim()) return;
    try {
      replaceLead(await apiClient.createLeadNote(activeLead.id, { text }));
      setNoteDraft("");
    } catch (error) {
      showToast(error.response?.data?.error || error.message || "Note creation failed", "error");
    }
  }

  function moveToScoping(lead) {
    setScoping((state) => [...state, {
      id: `lead-${lead.id}`,
      name: lead.name,
      owner: lead.owner,
      ownerId: lead.ownerId,
      stage: "1. New",
      priority: null,
      startDate: "",
      endDate: "",
      collaborators: [],
    }]);
    setDrawerLeadId(null);
    showToast("Moved to Scoping", lead.name);
    nav("scoping");
  }

  return (
    <div className="fade">
      {/* Page header */}
      <div className="px-9 pt-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="pg-title">Leads</div>
            <div className="pg-sub">Manage prospective clients through pipeline stages with tasks, notes, and tracking</div>
          </div>
          <Button onClick={() => setShowNewModal(true)}>+ New Lead</Button>
        </div>
      </div>

      <div className="px-9 pb-[52px] pt-5 flex flex-col gap-5">
        {/* KPIs */}
        <div className="grid grid-cols-5 gap-3.5">
          <KpiCard label="Total Leads" value={stats.total} />
          <KpiCard label="New" value={stats.new} tone="info" />
          <KpiCard label="In Contact" value={stats.contact} tone="warn" />
          <KpiCard label="Meetings Booked" value={stats.meeting} tone="success" />
          <KpiCard label="Pipeline Value" value={totalValue > 0 ? `${(totalValue / 1000).toFixed(0)}K kr` : "—"} tone="success" />
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <Select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)} className="max-w-[220px]">
            <option value="">All Owners</option>
            {owners.map((owner) => <option key={owner} value={owner}>{owner}</option>)}
          </Select>
          <div className="ml-auto text-[13.5px] text-rc-text-dim">
            <strong className="text-rc-text-secondary">{filtered.length}</strong> leads
          </div>
        </div>

        {/* Pipeline stages */}
        <div className="flex gap-1.5 overflow-x-auto py-4">
          <StageTile
            active={stageFilter === ""}
            onClick={() => setStageFilter("")}
            nameColor={C.sub}
            name="All"
            count={leads.length}
          />
          {LEAD_STATUSES.map((status) => {
            const count = leads.filter((lead) => lead.status === status).length;
            const fillPct = leads.length ? Math.round((count / leads.length) * 100) : 0;
            return (
              <StageTile
                key={status}
                active={stageFilter === status}
                onClick={() => setStageFilter(stageFilter === status ? "" : status)}
                nameColor={statusColor(status)}
                name={label(status)}
                count={count}
                fillPct={fillPct}
                fillColor={statusColor(status)}
              />
            );
          })}
        </div>

        {/* Card grid */}
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {filtered.map((lead) => {
            const tasks = lead.tasks || [];
            const done = tasks.filter((task) => task.done).length;
            const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
            const col = statusColor(lead.status);
            const pctColor = pct >= 100 ? C.green : pct >= 50 ? C.blue : col;
            return (
              <Card
                key={lead.id}
                variant="interactive"
                className="!p-4 min-h-[170px] relative"
                onClick={() => { setDrawerLeadId(lead.id); setTab("overview"); }}
              >
                {/* Left accent bar (Phase 3a — inline vs. old .ld-card-accent) */}
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-[10px]" style={{ background: col }} />
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-sm font-bold text-rc-text-primary">{lead.name}</div>
                  <div className="text-[10px] text-rc-blue-100 flex items-center gap-1.5">
                    <Avt initials={getInit(lead.owner)} size={18} />
                    {lead.owner || "Unassigned"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <CardTag color={col}>{label(lead.status)}</CardTag>
                  {lead.referral && <CardTag color="#8fa4c8">{lead.referral}</CardTag>}
                  {lead.industry && <CardTag color="#06b6d4">{lead.industry}</CardTag>}
                  {lead.region && <CardTag color="#a855f7">{lead.region}</CardTag>}
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="flex-1">
                    <ProgressBar pct={pct} color={pctColor} size="sm" />
                  </div>
                  <span className="text-[10px] font-bold min-w-[28px] text-right" style={{ color: pctColor }}>
                    {done}/{tasks.length}
                  </span>
                </div>
                {lead.notes?.[0] && (
                  <div className="text-[11px] italic text-[#4a6a96] truncate">{lead.notes[0].text}</div>
                )}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[rgba(20,33,61,0.5)]">
                  <span className="text-xs font-bold text-[#10c981]">{lead.estimatedValue || ""}</span>
                  <span className="text-[10px] text-[#4a6a96]">{lead.region || lead.contactPerson || ""}</span>
                </div>
              </Card>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="py-10 text-center text-rc-text-secondary border border-dashed border-rc-border rounded-2xl bg-rc-surface">
            No leads yet. Click <strong>+ New Lead</strong> to get started.
          </div>
        )}
      </div>

      {/* Drawer */}
      <SheetPrimitives.Sheet open={!!activeLead} onOpenChange={(open) => !open && setDrawerLeadId(null)}>
        {activeLead && (
          <SheetPrimitives.SheetContent side="right">
            <SheetPrimitives.SheetHeader>
              <SheetPrimitives.SheetTitle>{activeLead.name}</SheetPrimitives.SheetTitle>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <Chip size="sm" tone={statusTone(activeLead.status)}>{label(activeLead.status)}</Chip>
                <Chip size="sm" tone="muted">{LEAD_STAGE_DEPT[activeLead.status]?.dept || "Sales"}</Chip>
                <span className="text-[11.5px] text-rc-text-secondary">
                  {activeLead.owner || "Unassigned"} · {activeLead.referral || "No referral"}
                </span>
              </div>
            </SheetPrimitives.SheetHeader>

            {/* Tabs */}
            <div className="shrink-0 flex items-center gap-1 px-6 py-2 border-b border-rc-border bg-rc-surface">
              {[
                { id: "overview", label: "Overview" },
                { id: "tasks",    label: "Tasks",    count: activeLead.tasks?.length },
                { id: "history",  label: "History",  count: activeLead.stageHistory?.length },
                { id: "comments", label: "Comments", count: activeLead.notes?.length },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-[12.5px] font-semibold cursor-pointer transition-colors",
                    "flex items-center gap-1.5",
                    tab === t.id
                      ? "bg-[rgba(59,127,250,0.14)] text-rc-accent-strong"
                      : "text-rc-text-secondary hover:text-rc-text-primary",
                  )}
                >
                  {t.label}
                  {!!t.count && (
                    <span className={cn(
                      "font-mono text-[9.5px] px-1.5 rounded-[10px]",
                      tab === t.id ? "bg-[rgba(59,127,250,0.25)] text-rc-accent-strong" : "bg-rc-border text-rc-text-secondary",
                    )}>
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <SheetPrimitives.SheetBody>
              {tab === "overview" && (
                <div className="flex flex-col gap-6">
                  {/* Stage selector */}
                  <SheetSection title="Current Stage">
                    <div className="flex flex-wrap gap-2">
                      {LEAD_STATUSES.map((status) => {
                        const isCurrent = activeLead.status === status;
                        const col = statusColor(status);
                        return (
                          <button
                            key={status}
                            type="button"
                            onClick={() => { if (!isCurrent) changeStatus(activeLead, status); }}
                            className={cn(
                              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer",
                              "text-[11px] font-bold uppercase tracking-[0.04em] transition-colors",
                              isCurrent ? "cursor-default" : "hover:bg-rc-surface-hover",
                            )}
                            style={{
                              background: isCurrent ? `${col}1a` : "rgba(14,27,50,0.5)",
                              color: col,
                              borderColor: isCurrent ? col : "transparent",
                            }}
                          >
                            {isCurrent && <span className="text-[8px]">●</span>}
                            {label(status)}
                          </button>
                        );
                      })}
                    </div>
                  </SheetSection>

                  {/* Details */}
                  <SheetSection title="Details">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ["Owner", activeLead.owner],
                        ["Department", LEAD_STAGE_DEPT[activeLead.status]?.dept || "Sales"],
                        ["Contact Person", activeLead.contactPerson],
                        ["Referral", activeLead.referral],
                        ["Industry", activeLead.industry],
                        ["Region", activeLead.region],
                        ["Estimated Value", activeLead.estimatedValue],
                        ["Contact Email", activeLead.contactEmail],
                      ].map(([k, v]) => (
                        <Card key={k} variant="panel" className="px-3.5 py-3">
                          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-rc-text-secondary mb-1">{k}</div>
                          <div className="text-[13px] text-rc-text-primary">{v || "—"}</div>
                        </Card>
                      ))}
                    </div>
                  </SheetSection>

                  {/* Actions */}
                  <SheetSection title="Actions">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <Select
                        value={activeLead.ownerId || ""}
                        onChange={(event) => updateLead(activeLead.id, { ownerId: event.target.value || null })}
                        className="min-w-[240px] max-w-[320px]"
                      >
                        <option value="">Unassigned</option>
                        {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                      </Select>
                      {activeLead.status !== "LOST" && (
                        <Button variant="success" onClick={() => moveToScoping(activeLead)}>
                          → Move to Scoping
                        </Button>
                      )}
                    </div>
                  </SheetSection>
                </div>
              )}

              {tab === "tasks" && (
                <SheetSection title={`Tasks (${activeLead.tasks?.length || 0})`}>
                  {/*
                    Tasks tab still uses the .kb-task-* family from
                    scoping.css. Phase 4 migrates those to primitives;
                    until then the UI renders via legacy CSS.
                  */}
                  {(activeLead.tasks || []).length === 0 && (
                    <div className="text-sm text-rc-text-dim">No tasks yet.</div>
                  )}
                  {(activeLead.tasks || []).map((task) => {
                    const subtasks = task.subtasks || [];
                    const subtasksDone = subtasks.filter((s) => s.done).length;
                    const isExpanded = subtaskDrafts._expandedId === task.id;
                    return (
                      <div key={task.id} className={`kb-task-card${isExpanded ? " expanded" : ""}`}>
                        <div className="kb-task-row">
                          <div
                            className={`kb-task-check${task.done ? " done" : ""}`}
                            onClick={() => updateTask(task.id, { done: !task.done })}
                          >
                            {task.done && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>}
                          </div>
                          <button
                            className={`kb-task-label-btn${task.done ? " done" : ""}`}
                            onClick={() => setSubtaskDrafts((state) => ({ ...state, _expandedId: state._expandedId === task.id ? null : task.id }))}
                          >
                            {task.title}
                          </button>
                          <select className="kb-assignee-sel" value={task.assigneeId || ""} onChange={(event) => updateTask(task.id, { assigneeId: event.target.value || null })}>
                            <option value="">Assign…</option>
                            {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                          </select>
                          <input type="date" className="kb-due-input" value={task.dueDate || ""} onChange={(event) => updateTask(task.id, { dueDate: event.target.value || null })} />
                          {subtasks.length > 0 && <span className="kb-task-meta">{subtasksDone}/{subtasks.length} sub</span>}
                        </div>
                        {isExpanded && (
                          <div className="kb-task-detail">
                            {subtasks.map((subtask) => (
                              <div key={subtask.id} className="kb-subtask-row">
                                <div
                                  className={`kb-task-check${subtask.done ? " done" : ""}`}
                                  style={{ width: 14, height: 14, flexShrink: 0 }}
                                  onClick={() => updateSubtask(task.id, subtask.id, { done: !subtask.done })}
                                >
                                  {subtask.done && <span style={{ color: "#fff", fontSize: 8, lineHeight: 1 }}>✓</span>}
                                </div>
                                <span className={`kb-subtask-title${subtask.done ? " done" : ""}`}>{subtask.title}</span>
                                <select className="kb-assignee-sel" style={{ fontSize: "10px" }} value={subtask.assigneeId || ""} onChange={(event) => updateSubtask(task.id, subtask.id, { assigneeId: event.target.value || null })}>
                                  <option value="">Assign…</option>
                                  {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                                </select>
                                <input type="date" className="kb-due-input" style={{ fontSize: "10px" }} value={subtask.dueDate || ""} onChange={(event) => updateSubtask(task.id, subtask.id, { dueDate: event.target.value || null })} />
                              </div>
                            ))}
                            <div className="kb-subtask-add-row">
                              <input
                                className="kb-subtask-input"
                                placeholder="Add a subtask… (Enter to save)"
                                value={subtaskDrafts[task.id]?.title || ""}
                                onChange={(event) => setSubtaskDrafts((state) => ({ ...state, [task.id]: { ...(state[task.id] || emptyTaskDraft), title: event.target.value } }))}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" && event.target.value.trim()) {
                                    createSubtask(task.id);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="kb-new-task-row">
                    <input
                      placeholder="Add task…"
                      value={taskDraft.title}
                      onChange={(event) => setTaskDraft((state) => ({ ...state, title: event.target.value }))}
                      onKeyDown={(event) => { if (event.key === "Enter") createTask(); }}
                    />
                    <button onClick={createTask}>Add</button>
                  </div>
                </SheetSection>
              )}

              {tab === "history" && (
                <SheetSection title="Stage History">
                  <Timeline items={(activeLead.stageHistory || []).map((entry, index, arr) => ({
                    title: label(entry.stage),
                    meta: entry.enteredAt ? new Date(entry.enteredAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—",
                    body: entry.by ? `By ${entry.by}` : undefined,
                    tone: index === arr.length - 1 ? "active" : "default",
                  }))} />
                </SheetSection>
              )}

              {tab === "comments" && (
                <SheetSection title={`Comments (${activeLead.notes?.length || 0})`}>
                  {/* aud-comment-* classes are migrated in Phase 3c. */}
                  <div className="aud-comment-list">
                    {(activeLead.notes || []).length === 0 && <div className="text-sm text-rc-text-dim">No notes yet.</div>}
                    {(activeLead.notes || []).map((note) => (
                      <div key={note.id} className="aud-comment-row">
                        <div className="aud-comment-header">
                          <span className="aud-comment-author">{note.author || "Unknown"}</span>
                          <span className="aud-comment-ts">{note.createdAt ? new Date(note.createdAt).toISOString().slice(0, 10) : "—"}</span>
                        </div>
                        <CommentContent value={note.text} className="aud-comment-text rc-comment-body" />
                      </div>
                    ))}
                  </div>
                  <div className="aud-comment-compose">
                    <RichCommentInput
                      collapsible
                      rows={3}
                      placeholder="Add a note…"
                      value={noteDraft}
                      onChange={setNoteDraft}
                      onSubmit={createNote}
                      users={users}
                      teams={[]}
                      mentionScopeLabel="lead"
                      submitLabel="Post"
                    />
                  </div>
                </SheetSection>
              )}
            </SheetPrimitives.SheetBody>
          </SheetPrimitives.SheetContent>
        )}
      </SheetPrimitives.Sheet>

      {/* New lead modal */}
      <DialogPrimitives.Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogPrimitives.DialogContent className="max-w-[640px]">
          <DialogPrimitives.DialogHeader>
            <DialogPrimitives.DialogTitle>Add New Lead</DialogPrimitives.DialogTitle>
          </DialogPrimitives.DialogHeader>

          <div className="flex flex-col gap-4">
            <FieldGroup>
              <FieldLabel required>Company Name</FieldLabel>
              <Input
                placeholder="e.g. Acme Corp"
                value={newForm.name}
                onChange={(event) => setNewForm((state) => ({ ...state, name: event.target.value }))}
                autoFocus
              />
            </FieldGroup>

            <div className="grid grid-cols-2 gap-3">
              <FieldGroup>
                <FieldLabel>Owner</FieldLabel>
                <Select value={newForm.ownerId} onChange={(event) => setNewForm((state) => ({ ...state, ownerId: event.target.value }))}>
                  <option value="">Select...</option>
                  {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                </Select>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Referral Source</FieldLabel>
                <Input
                  placeholder="e.g. LinkedIn, referral name"
                  value={newForm.referral}
                  onChange={(event) => setNewForm((state) => ({ ...state, referral: event.target.value }))}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Industry</FieldLabel>
                <Input
                  placeholder="e.g. FinTech, Retail"
                  value={newForm.industry}
                  onChange={(event) => setNewForm((state) => ({ ...state, industry: event.target.value }))}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Region</FieldLabel>
                <Select value={newForm.region} onChange={(event) => setNewForm((state) => ({ ...state, region: event.target.value }))}>
                  <option value="">Select...</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Bahrain">Bahrain</option>
                  <option value="Other">Other</option>
                </Select>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Estimated Value</FieldLabel>
                <Input
                  placeholder="e.g. €50K"
                  value={newForm.estimatedValue}
                  onChange={(event) => setNewForm((state) => ({ ...state, estimatedValue: event.target.value }))}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Contact Person</FieldLabel>
                <Input
                  placeholder="e.g. John Smith"
                  value={newForm.contactPerson}
                  onChange={(event) => setNewForm((state) => ({ ...state, contactPerson: event.target.value }))}
                />
              </FieldGroup>
            </div>

            <Card variant="panel" className="p-3.5">
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-rc-text-secondary mb-2">
                Auto-Populated Tasks
              </div>
              <div className="text-[12px] text-rc-text-secondary leading-[1.7]">
                {(LEAD_STAGE_TASKS.NEW || []).map((task) => (
                  <div key={task.title}>• {task.title} <span style={{ color: C.dim }}>(Sales)</span></div>
                ))}
              </div>
            </Card>
          </div>

          <DialogPrimitives.DialogFooter>
            <Button variant="secondary" onClick={() => setShowNewModal(false)}>Cancel</Button>
            <Button onClick={createLead} disabled={saving || !newForm.name.trim()}>
              {saving ? "Creating…" : "Create Lead"}
            </Button>
          </DialogPrimitives.DialogFooter>
        </DialogPrimitives.DialogContent>
      </DialogPrimitives.Dialog>
    </div>
  );
}

// ── Local helpers (not exported) ──

function StageTile({ active, onClick, nameColor, name, count, fillPct, fillColor }) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "flex-1 min-w-[130px] rounded-[10px] border px-3 py-2.5 text-center cursor-pointer transition-colors",
        active
          ? "border-rc-accent bg-[rgba(59,127,250,0.06)]"
          : "border-rc-border bg-rc-surface hover:border-[#1e3a5f] hover:bg-[#0d1a30]",
      )}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.4px]" style={{ color: nameColor }}>{name}</div>
      <div className="mt-1 mb-0.5 text-[22px] font-extrabold text-rc-text-primary">{count}</div>
      {typeof fillPct === "number" && (
        <div className="h-[3px] rounded bg-[rgba(143,164,200,0.1)] overflow-hidden mt-1.5">
          <div className="h-full rounded transition-[width] duration-300" style={{ width: `${fillPct}%`, background: fillColor }} />
        </div>
      )}
    </div>
  );
}

function CardTag({ color, children }) {
  return (
    <span
      className="text-[9px] font-semibold px-2 py-0.5 rounded-[10px] uppercase tracking-[0.3px]"
      style={{ color, background: `${color}1a` }}
    >
      {children}
    </span>
  );
}

function SheetSection({ title, children }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-rc-text-secondary">{title}</div>
      {children}
    </section>
  );
}
